import React, { useState, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useGetAllRoleList, useGetUserMenusForRole, useInsertRoleMenusFromRef } from "../../../hooks";
import { Loading } from "../../atoms";
import { Table } from "../../molecules";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { IRoleMenu } from "../../../hooks/role-menu/useGetRoleMenuList";

export const RoleMenuTransferTab: React.FC = () => {
  const [sourceRoleId, setSourceRoleId] = useState<number | "">("");
  const [targetRoleId, setTargetRoleId] = useState<number | "">("");
  const [roleMenus, setRoleMenus] = useState<IRoleMenu[]>([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState<GridRowSelectionModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { data: rawRoleList, isLoading: rolesLoading } = useGetAllRoleList("ACTIVE");
  const { mutate: getUserMenus, isLoading: getUserMenusLoading } = useGetUserMenusForRole();
  const { mutate: insertRoleMenusFromRef, isLoading: transferLoading } = useInsertRoleMenusFromRef();
  const setSnackbar = useSetSnackBar();

  const roleList = useMemo(() => {
    return Array.isArray(rawRoleList?.data)
      ? rawRoleList.data.map((role: { name: string; id: number }) => ({
          label: role.name,
          value: role.id,
        }))
      : [];
  }, [rawRoleList?.data]);

  const handleSourceRoleChange = (event: SelectChangeEvent<number>) => {
    const roleId = event.target.value as number;
    setSourceRoleId(roleId);
    setSelectedMenuIds([]);
    
    if (roleId) {
      getUserMenus(
        { roleId },
        {
          onSuccess: (data) => {
            if (data.isSuccess) {
              setRoleMenus(data.data || []);
            } else {
              setRoleMenus([]);
              setSnackbar({
                severity: "error",
                description: data.message || "Rol menüleri alınamadı",
                isOpen: true,
              });
            }
          },
          onError: () => {
            setRoleMenus([]);
            setSnackbar({
              severity: "error",
              description: "Rol menüleri alınırken bir hata oluştu",
              isOpen: true,
            });
          },
        }
      );
    } else {
      setRoleMenus([]);
    }
  };

  const handleTargetRoleChange = (event: SelectChangeEvent<number>) => {
    const roleId = event.target.value as number;
    setTargetRoleId(roleId);
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      { field: "id", headerName: "ID", width: 100 },
      { field: "roleName", headerName: "Rol Adı", width: 200 },
      { field: "menuName", headerName: "Menü Adı", width: 200 },
      { field: "create", headerName: "Oluştur", width: 100, type: 'boolean' },
      { field: "read", headerName: "Oku", width: 100, type: 'boolean' },
      { field: "update", headerName: "Güncelle", width: 100, type: 'boolean' },
      { field: "delete", headerName: "Sil", width: 100, type: 'boolean' },
    ];
  }, []);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleTransfer = () => {
    if (!sourceRoleId || !targetRoleId) {
      setSnackbar({
        severity: "error",
        description: "Lütfen kaynak ve hedef rolleri seçiniz",
        isOpen: true,
      });
      return;
    }

    if (sourceRoleId === targetRoleId) {
      setSnackbar({
        severity: "error",
        description: "Kaynak ve hedef rol aynı olamaz",
        isOpen: true,
      });
      return;
    }

    if (selectedMenuIds.length === 0) {
      setSnackbar({
        severity: "warning",
        description: "Lütfen aktarılacak menüleri seçiniz",
        isOpen: true,
      });
      return;
    }

    const menuIds = selectedMenuIds.join(",");
    
    insertRoleMenusFromRef(
      {
        resourceRoleId: sourceRoleId as number,
        targetRoleId: targetRoleId as number,
        resourceIds: menuIds,
      },
      {
        onSuccess: (transferData) => {
          if (transferData.isSuccess) {
            setSnackbar({
              severity: "success",
              description: transferData.message || "Seçilen menüler başarıyla aktarıldı",
              isOpen: true,
            });
            setSelectedMenuIds([]);
          } else {
            setSnackbar({
              severity: "error",
              description: transferData.message || "Aktarım işlemi başarısız oldu",
              isOpen: true,
            });
          }
        },
        onError: (error) => {
          setSnackbar({
            severity: "error",
            description: "Aktarım işlemi sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };

  const isLoading = rolesLoading || getUserMenusLoading || transferLoading;
  const selectedRole = roleList.find((role: { label: string; value: number }) => role.value === sourceRoleId);

  return (
    <Box p={3}>
      {isLoading && <Loading />}
      
      <Typography variant="h6" gutterBottom>
        Rol Menü Seçerek Aktarma
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        Kaynak rolden istediğiniz menüleri seçerek hedef role aktarın.
      </Typography>

      <Stack spacing={3}>
        <Stack direction="row" spacing={3} maxWidth={800}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="source-role-select-label">Kaynak Rol</InputLabel>
            <Select
              labelId="source-role-select-label"
              value={sourceRoleId}
              label="Kaynak Rol"
              onChange={handleSourceRoleChange}
              disabled={isLoading}
            >
              {roleList.map((role: { label: string; value: number }) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="target-role-select-label">Hedef Rol</InputLabel>
            <Select
              labelId="target-role-select-label"
              value={targetRoleId}
              label="Hedef Rol"
              onChange={handleTargetRoleChange}
              disabled={isLoading}
            >
              {roleList.map((role: { label: string; value: number }) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {sourceRoleId && roleMenus.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {selectedRole?.label} Rolü Menüleri ({roleMenus.length} adet)
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip 
                  label={`${selectedMenuIds.length} seçildi`} 
                  color={selectedMenuIds.length > 0 ? "primary" : "default"}
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTransfer}
                  disabled={!sourceRoleId || !targetRoleId || selectedMenuIds.length === 0 || isLoading}
                  size="small"
                >
                  Aktar ({selectedMenuIds.length})
                </Button>
              </Stack>
            </Box>

            <Table
              columnVisibilityConfigKey="RoleMenuTransferListingColumnVisibilityKey"
              paginationModel={paginationModel}
              onPaginationModelChange={handleChangePagination}
              paginationMode="client"
              rowCount={roleMenus.length}
              sx={{ width: isDesktop ? 1308 : window.innerWidth - 50, height: 600 }}
              checkboxSelection
              onRowSelectionModelChange={setSelectedMenuIds}
              rowSelectionModel={selectedMenuIds}
              rows={roleMenus}
              columns={columns}
            />
          </>
        )}

        {sourceRoleId && roleMenus.length === 0 && !getUserMenusLoading &&(
          <Typography variant="body2" color="text.secondary">
            Seçilen rolde menü bulunmamaktadır.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
